import { Component, effect, inject, input } from '@angular/core';
import { CommonModule } from "@angular/common";
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { CheckableSettings, TreeItemLookup, TreeViewModule } from "@progress/kendo-angular-treeview";
import { ISensitivityLabelExpanded } from "../../../../../interfaces/i-sensitivity-label";
import { KeywordsControllerComponent } from "../../../../../shared/keywords-controller/keywords-controller.component";
import { MatIconModule } from "@angular/material/icon";
import { WorkflowBuilderService } from "../services/workflow-builder.service";
import { SENSITIVITY_LABELS_FLAT } from "../../../../../constants/sensitivity.constants";
import { RoutePath } from "../../../../../enums/api.enum";
import { KeywordService } from "../../../../../services/keyword.service";
import { computed } from "@angular/core";

/**
 * WorkflowDetailComponent manages the details step of the workflow builder.
 * It handles the form for workflow name, description, sensitivity labels, and keywords,
 * and synchronizes these details with the workflow service.
 */
@Component({
  selector: 'workflow-detail',
    imports: [
      CommonModule,
      ReactiveFormsModule,
      MatFormFieldModule,
      MatInputModule,
      MatButtonModule,
      MatIconModule,
      TreeViewModule,
      KeywordsControllerComponent
    ],
  templateUrl: './workflow-detail.component.html',
  styleUrl: './workflow-detail.component.scss'
})
export class WorkflowDetailComponent {
  buildAction = input.required(); // Input to trigger build action
  workflowForm!: FormGroup;

  // Hardcoded keywords (kept for reference/fallback)
  hardcodedKeywordStrings = [ 'Proposal', 'Contract', 'Invoice', 'Report', 'Meeting Notes', 'Presentation', 'Budget', 'Plan', 'Summary', 'Analysis' ];
  // Kendo UI TreeView
  expandedKeys: any[] = [ '0' ];
  checkedKeys: any[] = [];
  sensitivityLabelData: ISensitivityLabelExpanded[] = SENSITIVITY_LABELS_FLAT;
  checkableSettings: CheckableSettings = {
    checkChildren: true,
    checkDisabledChildren: true, // Check all children
    checkParents: true,
    enabled: true,  // Enable checkable mode
    mode: 'multiple', // Multiple selection mode
    checkOnClick: true, // Check all parents when children are checked
    uncheckCollapsedChildren: false // Uncheck all children when parent is unchecked,
  };
  isSensitivityLabelDisabled = (): boolean => {
    return false; // Placeholder for actual logic to determine if sensitivity labels should be disabled
  }
  // END Kendo UI TreeView

  #formBuilder = inject(FormBuilder);
  #workflowBuilderService = inject(WorkflowBuilderService);
  #keywordService = inject(KeywordService);
  #lastPatchedWorkflowId: string | null = null;

  // Merge API keywords with legacy hardcoded strings
  allKeywords = computed(() =>
    this.#keywordService.mergeWithLegacyStrings(this.hardcodedKeywordStrings)
  );
  isLoadingKeywords = this.#keywordService.isLoading;

  constructor() {
    // Load keywords from API
    this.#keywordService.loadKeywords();
    effect(() => {
      const workflow = this.#workflowBuilderService.workflow();
      const workflowId = workflow?.id ?? null;
      if (!this.workflowForm) {
        this.setForm();
        this.#lastPatchedWorkflowId = workflowId;
      } else if (workflowId && this.#lastPatchedWorkflowId !== workflowId) {
        // Patch the form with new workflow data if the workflowId changes
        this.patchFormWithWorkflow(workflow);
        this.#lastPatchedWorkflowId = workflowId;
      }
    });
  }

  /**
   * Decides whether the checkbox for the Sensitivity Label should be displayed for the item.
   */
  hasCheckbox = (item: any, index: string): boolean => {
    return !(item.parentId === 15 || item.id === 15);
  };

  /**
   * Handles the selection change event from the Sensitivity Label TreeView.
   */
  onSelectionChange(event: TreeItemLookup): void {
    this.workflowForm.get('sensitivityLabels')?.setValue([ ...this.checkedKeys ]);
    this.workflowForm.get('sensitivityLabels')?.markAsTouched();
    this.workflowForm.get('sensitivityLabels')?.updateValueAndValidity();
    this.onFieldBlur('sensitivityLabels');
  }

  /**
   * Validates the sensitivity labels field to ensure at least one label is selected.
   * Returns an error if no labels are selected.
   */
  sensitivityLabelValidator(control: AbstractControl): ValidationErrors | null {
    if (Array.isArray(control.value) && control.value.length === 0) {
      return { sensitivityLabelInvalid: true };
    }
    return null;
  }

  /**
   * Validates the form and returns whether it's valid or not.
   * Used by the parent component to determine if this step is complete.
   * @returns A boolean indicating whether the form is valid.
   */
  isValid(): boolean {
    if (!this.workflowForm) {
      return false;
    }

    this.workflowForm.markAllAsTouched();

    // Check if the form is valid and all required fields are filled
    return this.workflowForm.valid;
  }

  /**
   * Initializes the workflow form with values from the workflow service if available.
   * Sets up form controls for name, description, sensitivity labels, and keywords.
   */
  setForm(): void {
    if (this.#workflowBuilderService.workflow()) {
      // Load existing workflow data from the service if present
      const workflow = this.#workflowBuilderService.workflow();
      if (workflow?.details?.sensitivityLabels) {
        this.checkedKeys = (workflow.details.sensitivityLabels as any[]).map(label => label.id);
      }

      const workflowName = this.buildAction() === RoutePath.CloneWorkflow
        ? `(Clone) ${workflow?.details?.name ?? ''}`
        : workflow?.details?.name ?? '';

      this.workflowForm = this.#formBuilder.group({
        name: new FormControl(workflowName, Validators.required),
        description: new FormControl(workflow?.details?.description ?? '', Validators.required),
        sensitivityLabels: new FormControl(this.checkedKeys, [ this.sensitivityLabelValidator.bind(this) ]),
        keywords: new FormControl(workflow?.details?.keywords ?? [])
      });

      // Optionally patch other values if needed
      if (workflow) {
        this.workflowForm.patchValue({
          name: workflowName,
          description: workflow.details?.description,
          keywords: workflow.details?.keywords
        });
      }
    }
  }

  /**
   * Patches the workflow form with new workflow data.
   * Updates the form controls with the latest values from the provided workflow object,
   * including name, description, sensitivity labels, and keywords.
   *
   * @param workflow - The workflow object containing the latest details to patch into the form.
   */
  patchFormWithWorkflow(workflow: any) {
    if (!workflow) return;
    if (workflow?.details?.sensitivityLabels) {
      this.checkedKeys = (workflow.details.sensitivityLabels as any[]).map(label => label.id);
    }

    const workflowName = this.buildAction() === RoutePath.CloneWorkflow
      ? `(Clone) ${workflow?.details?.name ?? ''}`
      : workflow?.details?.name ?? '';

    this.workflowForm.patchValue({
      name: workflowName,
      description: workflow.details?.description ?? '',
      sensitivityLabels: this.checkedKeys,
      keywords: workflow.details?.keywords ?? []
    });
  }

  /**
   * Resets the workflow form to its initial state, clearing all values and validation states.
   * Also clears the checked sensitivity labels and validation error flag.
   */
  resetForm(): void {
    this.workflowForm.reset();
    this.workflowForm.markAsPristine();
    this.workflowForm.markAsUntouched();
    this.checkedKeys = [];
    this.workflowForm.get('sensitivityLabels')?.setValue([]);
    this.workflowForm.get('sensitivityLabels')?.updateValueAndValidity();
  }

  /**
   * Updates the workflow service with the current values from the workflow form.
   * Maps selected sensitivity label IDs to their full label objects before updating.
   */
  updateWorkflow(): void {
    // Map checkedKeys (ids) to full label objects
    const selectedLabels = this.checkedKeys
      .map(id => this.sensitivityLabelData.find(label => label.id === id))
      .filter(label => !!label);

    this.#workflowBuilderService.workflow.update(prev => ({
      ...prev,
      details: {
        ...prev?.details,
        ...this.workflowForm.value,
        sensitivityLabels: selectedLabels
      }
    }));
  }

  /**
   * Marks all controls in the workflow form as touched.
   * This is useful for triggering validation messages on all fields,
   * typically when the user attempts to submit the form.
   *
   * This is called programmatically in the validateAllChildForms method in the WorkflowBuilderComponent.
   */
  markFormAsTouched(): void {
    this.workflowForm.markAllAsTouched();
  }

  /**
   * Handles the blur event for a specific form control.
   * Marks the specified control as touched and updates the workflow state.
   *
   * This is needed when the user invalidates the control and clicks the save button.
   *
   * @param controlName - The name of the form control that lost focus.
   */
  onFieldBlur(controlName: string) {
    this.workflowForm.get(controlName)?.markAsTouched();
    this.updateWorkflow();
  }

  /**
   * Handler for the keywordsChanged event emitted by the KeywordsControllerComponent.
   * Called whenever the keywords control value changes in the child component.
   * Updates the workflow state with the latest form values.
   *
   * This is needed when the user invalidates the control and clicks the save button.
   */
  onKeywordsChanged() {
    this.updateWorkflow();
  }
}
